import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { putUser } from './actions/putUser';

// Edit user modal (Item 1) — persists via PUT /admin/user/:id (whitelisted firstName/
// lastName/email; backend enforces email uniqueness). Duplicate-email (409) and other errors
// are shown inline; on success the parent is notified to toast + refresh.
const EditUserModal = ({ open, user, onClose, onSaved }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
    }
    setError('');
  }, [user]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    const result = await putUser(user.id, form);
    setSaving(false);
    if (result.ok) {
      onSaved(`Saved changes to ${form.firstName} ${form.lastName}.`);
      onClose();
    } else {
      setError(result.message || 'Failed to save user.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit user</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <MuiAlert severity="error" style={{ marginBottom: 12 }}>
            {error}
          </MuiAlert>
        ) : null}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="First name" fullWidth value={form.firstName} onChange={set('firstName')} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Last name" fullWidth value={form.lastName} onChange={set('lastName')} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Email" type="email" fullWidth value={form.email} onChange={set('email')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

EditUserModal.defaultProps = { user: null };

export default EditUserModal;
